/**
 * HR-initiated CandidateApplication intake form (Phase 1).
 *
 * Scope:
 *   - Captures the minimum intake dataset required to open a pre-onboarding
 *     workflow for a candidate who did NOT come in through the ATS webhook.
 *   - Track-aware: Work Permit / S Pass or EP / Local or PR.
 *   - Writes straight to /confidential/pre-onboarding/applications/ and
 *     redirects to the candidate's detail page on success.
 *
 * Out of scope for Phase 1:
 *   - Intended allowances (captured on the detail page later).
 *   - Offer letter upload (Phase 2).
 *   - Manager assignment via reports_to (optional, not enforced).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from '../../utils/toast';
import { ArrowLeft, Save, AlertTriangle } from 'lucide-react';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import FormSelect from '../../components/UI/FormSelect';
import preOnboardingAPI, {
  TRACK_WORK_PERMIT,
  TRACK_S_PASS_EP,
  TRACK_LOCAL_PR,
  TRACK_LABELS,
} from '../../services/preOnboardingApi';
import api from '../../services/api';

// Inline style tokens reused across form rows. We centralise them up-front
// rather than retype the long Tailwind strings because the design system
// (see docs/frontend/src/styles/design-system-reference.md) mandates a very
// specific checkbox treatment on admin pages — the native browser blue tick
// is non-compliant without `accent-red-600`.
const LABEL_CLS = 'block text-sm font-medium text-gray-700 mb-2';
const CHECKBOX_CLS =
  'rounded border-gray-300 accent-red-600 text-red-600 focus-visible:ring-red-500';

// Mirrors default_medical_required() in staff_confidential.pre_onboarding_models.
// Keeping the logic client-side as well is strictly for UI affordance — the
// backend stays the source of truth and will still reject overrides without
// a reason.
const SG_NATIONALITY = /^(singapor|sg$|s'pore)/i;
const computeDefaultMedicalRequired = (track, nationality) => {
  if (!track) return false;
  if (track === TRACK_WORK_PERMIT) return true;
  if (track === TRACK_S_PASS_EP) {
    return !!(nationality && !SG_NATIONALITY.test(nationality.trim()));
  }
  return false;
};

const INITIAL_STATE = {
  track: TRACK_LOCAL_PR,
  full_name: '',
  personal_email: '',
  personal_mobile: '',
  date_of_birth: '',
  nationality: '',
  designation: '',
  department: '',
  offered_start_date: '',
  offered_salary: '',
  employment_arrangement: 'standard',
  probation_months: 3,
  mcf_posting_required: false,
  medical_required: false,
  medical_required_reason: '',
  source: 'hr_manual',
  hr_notes: '',
  ats_candidate_id: '',
};

const NewPreOnboarding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [medicalTouched, setMedicalTouched] = useState(false);

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      try {
        const resp = await api.get('/configuration/departments/');
        if (!ignore) {
          const rows = Array.isArray(resp.data) ? resp.data : (resp.data?.results || []);
          setDepartments(rows);
        }
      } catch (err) {
        console.error('Failed to load departments', err);
        toast.error('Could not load departments — please reload.');
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    load();
    return () => { ignore = true; };
  }, []);

  // Auto-suggest medical_required based on track + nationality, until the user
  // touches the medical toggle (then we respect their choice).
  useEffect(() => {
    if (medicalTouched) return;
    const suggested = computeDefaultMedicalRequired(formData.track, formData.nationality);
    if (formData.medical_required !== suggested) {
      setFormData((prev) => ({ ...prev, medical_required: suggested }));
    }
  }, [formData.track, formData.nationality, formData.medical_required, medicalTouched]);

  const trackOptions = useMemo(() => [
    { value: TRACK_LOCAL_PR, label: TRACK_LABELS[TRACK_LOCAL_PR] },
    { value: TRACK_S_PASS_EP, label: TRACK_LABELS[TRACK_S_PASS_EP] },
    { value: TRACK_WORK_PERMIT, label: TRACK_LABELS[TRACK_WORK_PERMIT] },
  ], []);

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!formData.track) e.track = 'Track is required.';
    if (!formData.full_name.trim()) e.full_name = 'Full name is required.';
    if (!formData.personal_email.trim()) e.personal_email = 'Personal email is required.';
    if (!formData.personal_mobile.trim()) e.personal_mobile = 'Mobile is required.';
    if (!formData.date_of_birth) e.date_of_birth = 'Date of birth is required.';
    if (!formData.nationality.trim()) e.nationality = 'Nationality is required.';
    if (!formData.designation.trim()) e.designation = 'Designation is required.';
    if (!formData.department) e.department = 'Department is required.';
    if (!formData.offered_start_date) e.offered_start_date = 'Start date is required.';
    if (!formData.offered_salary || Number(formData.offered_salary) <= 0) {
      e.offered_salary = 'Offered salary must be greater than zero.';
    }
    const defaultMed = computeDefaultMedicalRequired(formData.track, formData.nationality);
    if (formData.medical_required !== defaultMed && !formData.medical_required_reason.trim()) {
      e.medical_required_reason =
        'HR override requires a reason when changing the suggested medical flag.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error('Please fix the highlighted fields.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        offered_salary: Number(formData.offered_salary),
        probation_months: Number(formData.probation_months) || 3,
      };
      if (!payload.ats_candidate_id) delete payload.ats_candidate_id;
      if (!payload.medical_required_reason) delete payload.medical_required_reason;

      const resp = await preOnboardingAPI.createApplication(payload);
      const created = resp.data;
      toast.success(`Application ${created.public_ref} created.`);
      navigate(`/admin/employee-lifecycle/onboarding/${encodeURIComponent(created.public_ref)}`);
    } catch (err) {
      console.error('Create candidate application failed', err);
      const backendErrors = err?.response?.data || {};
      if (typeof backendErrors === 'object' && !Array.isArray(backendErrors)) {
        setErrors((prev) => ({ ...prev, ...backendErrors }));
      }
      toast.error(
        backendErrors.detail ||
          backendErrors.non_field_errors?.[0] ||
          'Could not save — see field errors.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading intake form…" />;

  const showMCF = formData.track === TRACK_S_PASS_EP;
  const showATSId = formData.source === 'ats';
  const medicalSuggested = computeDefaultMedicalRequired(formData.track, formData.nationality);
  const medicalIsOverride = formData.medical_required !== medicalSuggested;

  return (
    // Layout (AdminLayout) provides p-4 md:p-6 and the gray background already,
    // so the page root must not add its own padding or a max-width cap. We keep
    // vertical rhythm via space-y-8 to match other admin pages
    // (EmployeeLifecycle, etc.) and let the cards stretch to the full content
    // width. Inside each card, the grid ramps up to 3 columns on xl screens so
    // the intake form actually uses the width instead of leaving ~50% empty.
    <div className="min-h-screen bg-gray-50 space-y-8">
      {/* Header — admin theme (red accents), full-width flex row */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/employee-lifecycle')}
            aria-label="Back to Employee Lifecycle"
            title="Back to Employee Lifecycle"
            className="icon-button-admin flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate mb-1">New Onboarding</h1>
            <p className="text-xs sm:text-sm text-gray-500">
              Create a candidate application that did not come through the ATS webhook.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <section className="bg-white rounded-2xl border shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Track &amp; Source</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div>
              <label className={LABEL_CLS}>Track *</label>
              <FormSelect
                theme="admin"
                options={trackOptions}
                value={formData.track}
                onChange={(val) => setField('track', val)}
                required
              />
              {errors.track && <p className="text-xs text-red-600 mt-1">{errors.track}</p>}
            </div>
            <div>
              <label className={LABEL_CLS}>Source</label>
              <FormSelect
                theme="admin"
                options={[
                  { value: 'hr_manual', label: 'HR Manual' },
                  { value: 'candidate_portal', label: 'Candidate Portal' },
                  { value: 'ats', label: 'ATS (retrospective)' },
                ]}
                value={formData.source}
                onChange={(val) => setField('source', val)}
              />
            </div>
            {showATSId && (
              <div>
                <label className={LABEL_CLS}>ATS Candidate ID</label>
                <input
                  type="text"
                  value={formData.ats_candidate_id}
                  onChange={(e) => setField('ats_candidate_id', e.target.value)}
                  className="input-admin"
                  placeholder="e.g. ATS-00001"
                />
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-2xl border shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Candidate</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div className="xl:col-span-3 md:col-span-2">
              <label className={LABEL_CLS}>Full Name *</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setField('full_name', e.target.value)}
                className="input-admin"
              />
              {errors.full_name && <p className="text-xs text-red-600 mt-1">{errors.full_name}</p>}
            </div>
            <div>
              <label className={LABEL_CLS}>Personal Email *</label>
              <input
                type="email"
                value={formData.personal_email}
                onChange={(e) => setField('personal_email', e.target.value)}
                className="input-admin"
              />
              {errors.personal_email && <p className="text-xs text-red-600 mt-1">{errors.personal_email}</p>}
            </div>
            <div>
              <label className={LABEL_CLS}>Mobile *</label>
              <input
                type="tel"
                value={formData.personal_mobile}
                onChange={(e) => setField('personal_mobile', e.target.value)}
                className="input-admin"
                placeholder="+65 9xxx xxxx"
              />
              {errors.personal_mobile && <p className="text-xs text-red-600 mt-1">{errors.personal_mobile}</p>}
            </div>
            <div>
              <label className={LABEL_CLS}>Date of Birth *</label>
              <input
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => setField('date_of_birth', e.target.value)}
                className="input-admin"
              />
              {errors.date_of_birth && <p className="text-xs text-red-600 mt-1">{errors.date_of_birth}</p>}
            </div>
            <div>
              <label className={LABEL_CLS}>Nationality *</label>
              <input
                type="text"
                value={formData.nationality}
                onChange={(e) => setField('nationality', e.target.value)}
                className="input-admin"
                placeholder="Singaporean / Malaysian / …"
              />
              {errors.nationality && <p className="text-xs text-red-600 mt-1">{errors.nationality}</p>}
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Offer</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div>
              <label className={LABEL_CLS}>Designation *</label>
              <input
                type="text"
                value={formData.designation}
                onChange={(e) => setField('designation', e.target.value)}
                className="input-admin"
              />
              {errors.designation && <p className="text-xs text-red-600 mt-1">{errors.designation}</p>}
            </div>
            <div>
              <label className={LABEL_CLS}>Department *</label>
              <FormSelect
                theme="admin"
                options={[
                  { value: '', label: 'Select a department…' },
                  ...departments.map((dept) => ({
                    value: String(dept.id),
                    label: dept.department_name,
                  })),
                ]}
                value={formData.department ? String(formData.department) : ''}
                onChange={(val) => setField('department', val)}
                placeholder="Select a department…"
                required
              />
              {errors.department && <p className="text-xs text-red-600 mt-1">{errors.department}</p>}
            </div>
            <div>
              <label className={LABEL_CLS}>Offered Start Date *</label>
              <input
                type="date"
                value={formData.offered_start_date}
                onChange={(e) => setField('offered_start_date', e.target.value)}
                className="input-admin"
              />
              {errors.offered_start_date && <p className="text-xs text-red-600 mt-1">{errors.offered_start_date}</p>}
            </div>
            <div>
              <label className={LABEL_CLS}>Offered Monthly Salary (SGD) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.offered_salary}
                onChange={(e) => setField('offered_salary', e.target.value)}
                className="input-admin"
              />
              {errors.offered_salary && <p className="text-xs text-red-600 mt-1">{errors.offered_salary}</p>}
            </div>
            <div>
              <label className={LABEL_CLS}>Employment Arrangement</label>
              <FormSelect
                theme="admin"
                options={[
                  { value: 'standard', label: 'Standard' },
                  { value: 'flexible', label: 'Flexible' },
                  { value: 're_employment', label: 'Re-employment' },
                ]}
                value={formData.employment_arrangement}
                onChange={(val) => setField('employment_arrangement', val)}
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Probation (months)</label>
              <input
                type="number"
                min="0"
                max="12"
                value={formData.probation_months}
                onChange={(e) => setField('probation_months', e.target.value)}
                className="input-admin"
              />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl border shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Compliance</h2>
          {showMCF && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.mcf_posting_required}
                onChange={(e) => setField('mcf_posting_required', e.target.checked)}
                className={CHECKBOX_CLS}
              />
              MCF posting required (MOM EFMA 14-day rule)
            </label>
          )}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.medical_required}
                onChange={(e) => {
                  setMedicalTouched(true);
                  setField('medical_required', e.target.checked);
                }}
                className={CHECKBOX_CLS}
              />
              Medical examination required
              <span className="ml-2 text-xs text-gray-500">
                (suggested: {medicalSuggested ? 'yes' : 'no'} for {TRACK_LABELS[formData.track]})
              </span>
            </label>
            {medicalIsOverride && (
              <div className="border-l-4 border-amber-400 bg-amber-50 p-3 rounded text-sm">
                <div className="flex items-center text-amber-700 font-medium mb-1">
                  <AlertTriangle className="w-4 h-4 mr-1" /> HR override reason required
                </div>
                <textarea
                  value={formData.medical_required_reason}
                  onChange={(e) => setField('medical_required_reason', e.target.value)}
                  className="textarea-admin"
                  rows={2}
                  placeholder="e.g. Food-handling role — requires medical clearance regardless of pass type."
                />
                {errors.medical_required_reason && (
                  <p className="text-xs text-red-600 mt-1">{errors.medical_required_reason}</p>
                )}
              </div>
            )}
          </div>
        </section>

        <section className="bg-white rounded-2xl border shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">HR Notes</h2>
          <textarea
            value={formData.hr_notes}
            onChange={(e) => setField('hr_notes', e.target.value)}
            className="textarea-admin"
            rows={3}
            placeholder="Internal notes for the onboarding team."
          />
        </section>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            className="btn btn-admin-outline"
            onClick={() => navigate('/admin/employee-lifecycle')}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-admin-primary"
            disabled={submitting}
          >
            <Save className="w-4 h-4 mr-2" />
            {submitting ? 'Saving…' : 'Create Application'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewPreOnboarding;
